/** The owner↔doorman chat at Home (owner decision 2026-08-03). Split module, barrel re-exports. */
import { API_URL, call, get, num, post } from "./core";
import { PLAN_TIERS as SHARED_PLAN_TIERS } from "@nob/schemas";
import { toAttachment, type Attachment } from "./attachment";
import { normaliseHearingRoads, type HearingRoads } from "./plan";
import { parseFrame } from "../sse";

export type DoormanRole = "owner" | "doorman" | "notice";

export type DoormanMessage = {
  id: string;
  role: DoormanRole;
  content: string;
  /** A notice carrying a proposal renders as the accept/dismiss card. */
  proposal_id: string | null;
  /**
   * WHICH card: a rules change to review, a bio the doorman drafted
   * (FR-3.8.1), or a public line it drafted (R-0925-2 (5)). They have different
   * verbs and different endpoints, so a card that guessed would offer the wrong
   * one after a reload.
   */
  proposal_kind: "rules" | "bio" | "public_line";
  /** The card's facts, when the server attached them: the change itself… */
  proposal_diff: string | null;
  /**
   * …and where it stands, so a resolved card never offers Accept again.
   *
   * `superseded` is its own answer rather than a shade of `dismissed` (ruling
   * R-0915-1 (4)): the owner did not say no to that card, they said "like this
   * instead", and the row it left behind has to say which — otherwise a reload
   * turns every adjusted card into a refused one.
   */
  proposal_status: "pending" | "accepted" | "dismissed" | "superseded" | null;
  /**
   * A file the owner put in the thread, in place of words (owner decision
   * 2026-08-23). The server interleaves these rows with the spoken ones by
   * their clock, so a row is either something said or something attached and
   * the renderer picks the bubble by which of the two it is.
   */
  attachment: Attachment | null;
  /**
   * THE ROOM A NOTICE IS ABOUT (ruling R-0910-7 rule 1). A door that opened for
   * this person is news on their desk, and the news is worth nothing without a
   * way in — so a notice carrying a knock draws as a row that opens that room.
   * `null` on everything else, which is nearly every row.
   */
  knock_id: string | null;
  created_at: string | null;
};

export type SetupQuestion =
  | { key: "gets"; kind: "chips"; chips: { label: string }[] }
  | { key: "doesnt"; kind: "chips"; chips: { label: string; severity: "hard" | "soft" }[] }
  | { key: "who"; kind: "text" }
  | { key: "name"; kind: "text" }
  | { key: "tone"; kind: "chips_one"; chips: { label: string }[] }
  | { key: "cap"; kind: "chips_one"; chips: { label: string }[] };

export type SetupSuggestions = { pending: SetupQuestion[] };

/**
 * The things a new owner has not done yet (FR-11). Not a checklist widget: they
 * arrive as the doorman's own standing prompts in the thread, one row and one
 * button each.
 *
 * THERE ARE TWO, NOT THREE (ruling R-0826-3). `set_contact_email` is gone: the
 * address is collected inside the onboarding run and proved by a six-digit code
 * before the run advances, so by the time a doorman thread exists the job is
 * already done. A prompt that is born answered is a prompt that only ever
 * renders as a bug.
 *
 * `done` is the SERVER's word and only ever the server's. A prompt is answered
 * by its own action completing — a door handed out, a rules version written —
 * never by a panel having been opened, and never by this client deciding it
 * looked finished. So nothing here is written locally: the action lands, the
 * thread is re-read, and the prompt goes when the server says it has gone.
 */
export const PROMPT_ACTIONS = ["share_door", "edit_rules"] as const;

export type PromptAction = (typeof PROMPT_ACTIONS)[number];

export type DoormanPrompt = { action: PromptAction; done: boolean };

export type DoormanThread = {
  /** The setup questions still worth asking; null once both are answered. */
  setup: SetupSuggestions | null;
  /**
   * The standing prompts, in the server's order — present once the setup
   * questions are done, empty before that. The order is FIXED and belongs to
   * the server: it is the order the jobs are worth doing in.
   */
  prompts: DoormanPrompt[];
  persona_name: string;
  /**
   * THE AI DISCLOSURE over the owner's own thread (EU AI Act Art. 50; ruling
   * R-0914-1 (11)). Server-authored, the same bytes on every read, rendered
   * above the log. Empty when the payload could not be read: nothing is drawn
   * rather than a sentence this side invented.
   */
  disclosure: string;
  handle: string;
  /**
   * The meter under the composer, as a SHARE of this period's plan allowance
   * and a flag for the lane change past it (docs/plans.md §5.1, §5.3). There is
   * no capacity here and no per-turn price: capacities stay server-side, and
   * the accounting is the share itself, after the work (PRD v3.3 §1.5a).
   */
  plan: DoormanPlanMeter;
  /**
   * Whether a House Rules conversation is running, AS THE SERVER DERIVES IT
   * (ruling R-0915-1). The console used to remember this itself and lost it on
   * every reload. It is a fact about the thread, so it is read off the thread,
   * here and on every turn's `done` frame.
   */
  onboarding: OnboardingRun;
  messages: DoormanMessage[];
};

export type OnboardingRun = {
  running: boolean;
  /** The owner's own turns since the ask, the ask counted as the first. */
  turns: number;
  /** The batch proposal has gone up — the conversation's own ending. */
  proposed: boolean;
};

function normaliseOnboarding(raw: unknown): OnboardingRun {
  const w = (raw ?? {}) as Record<string, unknown>;
  return {
    running: w["running"] === true,
    turns: num(w["turns"]),
    proposed: w["proposed"] === true,
  };
}

export type DoormanPlanMeter = {
  tier: "free" | "lite" | "personal" | "pro5" | "pro20";
  /** False on Free (ruling C10): this tier does no AI work at all. */
  metered: boolean;
  /** How much of this window's allowance is spent — past 1 beyond it (R-0923-3 (8)). */
  used_share: number;
  /** The allowance is used; further turns wait for it to reset (ToU 2.2 §5.3). */
  overflow: boolean;
};

// One list, from `@nob/schemas` (2026-09-08) — see `lib/api/plan.ts`.
const PLAN_TIERS = SHARED_PLAN_TIERS;

function normalisePlanMeter(raw: unknown): DoormanPlanMeter {
  const m = (raw ?? {}) as Record<string, unknown>;
  const tier = PLAN_TIERS.find((known) => known === m["tier"]) ?? "free";
  const share = num(m["used_share"]);
  return {
    tier,
    // Defaults TRUE: a payload from a server that predates the field reads as a
    // metered plan rather than silently rendering every tier as Free.
    metered: m["metered"] !== false,
    used_share: Math.max(share, 0),
    overflow: m["overflow"] === true,
  };
}

export type DoormanThreadResult =
  | { ok: true; thread: DoormanThread }
  | { ok: false; code: "UNAUTHENTICATED" | "DOOR_NOT_FOUND" | "UNKNOWN" };

const ROLES: DoormanRole[] = ["owner", "doorman", "notice"];

function normaliseDoormanMessage(raw: unknown): DoormanMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  const id = typeof m["id"] === "string" ? m["id"] : "";
  const role = ROLES.find((known) => known === m["role"]);
  const content = typeof m["content"] === "string" ? m["content"] : "";
  if (!id || !role) return null;
  const status = m["proposal_status"];
  return {
    id,
    role,
    content,
    proposal_id: typeof m["proposal_id"] === "string" ? m["proposal_id"] : null,
    proposal_kind:
      m["proposal_kind"] === "bio" || m["proposal_kind"] === "public_line"
        ? m["proposal_kind"]
        : "rules",
    proposal_diff: typeof m["proposal_diff"] === "string" ? m["proposal_diff"] : null,
    // The server speaks in proposal lifecycle terms; the card only needs
    // "still asking" or the verdict. Everything settled reads as its verdict.
    proposal_status:
      status === "accepted" || status === "edited"
        ? "accepted"
        : status === "superseded"
          ? "superseded"
          : status === "dismissed"
            ? "dismissed"
            : status === "queued" || status === "surfaced" || status === "pending"
              ? "pending"
              : null,
    attachment:
      m["attachment"] && typeof m["attachment"] === "object"
        ? toAttachment(m["attachment"] as Record<string, unknown>)
        : null,
    knock_id: typeof m["knock_id"] === "string" && m["knock_id"] ? m["knock_id"] : null,
    created_at: typeof m["created_at"] === "string" ? m["created_at"] : null,
  };
}

export async function fetchDoormanThread(before?: string): Promise<DoormanThreadResult> {
  const r = await call(() =>
    get(`/api/v1/doorman/thread${before ? `?before=${encodeURIComponent(before)}` : ""}`),
  );
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (r.res.status === 404) return { ok: false, code: "DOOR_NOT_FOUND" };
  if (!r.res.ok) return { ok: false, code: "UNKNOWN" };

  const b = r.body as Record<string, unknown>;
  const messages: DoormanMessage[] = [];
  if (Array.isArray(b["messages"])) {
    for (const item of b["messages"]) {
      const message = normaliseDoormanMessage(item);
      if (message) messages.push(message);
    }
  }
  return {
    ok: true,
    thread: {
      setup: normaliseSetup(b["setup"]),
      prompts: normalisePrompts(b["prompts"]),
      persona_name: typeof b["persona_name"] === "string" ? b["persona_name"] : "Nob",
      disclosure: typeof b["disclosure"] === "string" ? b["disclosure"] : "",
      handle: typeof b["handle"] === "string" ? b["handle"] : "",
      plan: normalisePlanMeter(b["plan"]),
      onboarding: normaliseOnboarding(b["onboarding"]),
      messages,
    },
  };
}

/**
 * The prompts, in the order they arrived. A prompt this build has no button for
 * is dropped rather than rendered as a mystery — the same rule the channel rows
 * follow — and `done` is true only when the server said the word.
 */
function normalisePrompts(raw: unknown): DoormanPrompt[] {
  if (!Array.isArray(raw)) return [];
  const prompts: DoormanPrompt[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const p = item as Record<string, unknown>;
    const action = PROMPT_ACTIONS.find((known) => known === p["action"]);
    if (!action) continue;
    prompts.push({ action, done: p["done"] === true });
  }
  return prompts;
}

/**
 * The one prompt whose completion only the CLIENT can witness: handing the door
 * out happens in a clipboard or in somebody else's composer, and no request to
 * this API follows it. So the screen tells the server it happened — and only
 * after it actually did (a copy that landed, a post handed over), never when the
 * share panel was merely opened.
 *
 * The other two need no call at all: an address saved and a rules version
 * written are both things the server observes for itself.
 */
export async function markShareDoorDone(): Promise<
  { ok: true } | { ok: false; code: "UNAUTHENTICATED" | "UNKNOWN" }
> {
  const r = await call(() => post("/api/v1/door/prompts/share_door/done", {}));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (!r.res.ok) return { ok: false, code: "UNKNOWN" };
  return { ok: true };
}

function normaliseSetup(raw: unknown): SetupSuggestions | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r["pending"])) return null;
  const pending: SetupQuestion[] = [];
  for (const item of r["pending"]) {
    const q = item as Record<string, unknown>;
    const key = q["key"];
    const chips = (Array.isArray(q["chips"]) ? q["chips"] : []).flatMap((x) => {
      const c = x as Record<string, unknown>;
      const label = c?.["label"];
      if (typeof label !== "string" || label.length === 0) return [];
      return [
        { label, severity: c["severity"] === "soft" ? ("soft" as const) : ("hard" as const) },
      ];
    });
    if (key === "gets") pending.push({ key, kind: "chips", chips });
    else if (key === "doesnt") pending.push({ key, kind: "chips", chips });
    else if (key === "who" || key === "name") pending.push({ key, kind: "text" });
    else if (key === "tone" || key === "cap") pending.push({ key, kind: "chips_one", chips });
  }
  return pending.length > 0 ? { pending } : null;
}

/** A chip answer to a setup question — or `done`, the durable "leave it
 * open". Deterministic on the server, free. */
export async function applyDoormanSetup(input: {
  gets?: string[];
  doesnt?: { text: string; severity: "hard" | "soft" }[];
  who?: string;
  name?: string;
  tone?: string;
  cap?: number | null;
  skip?: string;
  done?: boolean;
}): Promise<{ ok: true; version: number } | { ok: false; code: string }> {
  const r = await call(() => post("/api/v1/doorman/setup", input));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (!r.res.ok) {
    const code = (r.body as { error?: { code?: string } }).error?.code;
    return { ok: false, code: code ?? "UNKNOWN" };
  }
  return { ok: true, version: num((r.body as Record<string, unknown>)["version"]) };
}

export type DoormanTurnEvent =
  | { type: "token"; delta: string }
  | { type: "notice"; content: string }
  | { type: "working"; content: string }
  | {
      type: "proposal";
      proposal_id: string;
      diff: string;
      change_note: string;
      /** Every section of the document this change touches (R-0915-1 (3)). */
      sections: string[];
      /** The card this one replaces, when the owner adjusted it; else null. */
      supersedes: string | null;
    }
  | {
      type: "bio_proposal";
      proposal_id: string;
      bio: string;
      change_note: string;
      supersedes: string | null;
    }
  /** A public line Nob drafted (R-0925-2 (5)) — the bio card's twin. */
  | {
      type: "public_line_proposal";
      proposal_id: string;
      public_line: string;
      change_note: string;
      supersedes: string | null;
    }
  | {
      type: "done";
      message_id: string;
      proposal_id: string | null;
      /**
       * The run, as the server derives it after this turn's rows landed. Null
       * from a frame that did not carry it, which says nothing about the run
       * and must not be read as "it ended".
       */
      onboarding: OnboardingRun | null;
    }
  | {
      type: "error";
      code: string;
      /**
       * The roads, when the refusal was `PLAN_EXHAUSTED`. It carried a
       * shortfall and a list of packs until ruling R-0916-1 deleted both, and
       * the priced reset until wave 57 (the reset itself is deleted, ruling
       * R-0923-3 (1)): a conversation carries no price, so this is the
       * hearing's own unpriced shape, and the offer is the plan sheet's.
       */
      exhausted: HearingRoads | null;
    };

/**
 * One streamed turn with the doorman. The same fetch-and-read shape as the
 * knock's `streamTurn`; the events differ, so the frames are mapped here.
 */
export async function* streamDoormanTurn(
  message: string,
  signal?: AbortSignal,
  /**
   * The card the owner is adjusting (ruling R-0915-1 (4)). Adjust never opens
   * the editor: it is an ordinary turn that names the card it revises, and the
   * reply is a new card that supersedes it.
   */
  adjustOf?: string | null,
  /**
   * A VOICE NOTE SENT AS THE TURN (wave 76), in place of `message`: the id the
   * upload answered with. Its transcript is the owner's line.
   */
  voiceAttachmentId?: string | null,
): AsyncGenerator<DoormanTurnEvent> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/doorman/turn`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "text/event-stream" },
      body: JSON.stringify({
        ...(voiceAttachmentId ? { voice_attachment_id: voiceAttachmentId } : { message }),
        ...(adjustOf ? { adjust_of: adjustOf } : {}),
      }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", exhausted: null };
    return;
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: unknown; details?: unknown };
    };
    const code =
      res.status === 401
        ? "UNAUTHENTICATED"
        : typeof body.error?.code === "string"
          ? body.error.code
          : "UNKNOWN";
    const d = (
      body.error?.details && typeof body.error.details === "object" ? body.error.details : {}
    ) as Record<string, unknown>;
    yield {
      type: "error",
      code,
      exhausted: code === "PLAN_EXHAUSTED" ? normaliseHearingRoads(d) : null,
    };
    return;
  }
  if (!res.body) {
    yield { type: "error", code: "NETWORK", exhausted: null };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      for (;;) {
        const at = buffer.indexOf("\n\n");
        if (at === -1) break;
        const frame = parseFrame(buffer.slice(0, at));
        buffer = buffer.slice(at + 2);
        const event = frame && toDoormanEvent(frame);
        if (event) yield event;
      }
    }
  } catch {
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", exhausted: null };
  } finally {
    reader.releaseLock();
  }
}

function toDoormanEvent(frame: { event: string; data: string }): DoormanTurnEvent | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(frame.data) as Record<string, unknown>;
  } catch {
    return null;
  }
  switch (frame.event) {
    case "token":
      return typeof data["delta"] === "string" ? { type: "token", delta: data["delta"] } : null;
    case "notice":
      return typeof data["content"] === "string"
        ? { type: "notice", content: data["content"] }
        : null;
    case "working":
      return typeof data["content"] === "string"
        ? { type: "working", content: data["content"] }
        : null;
    case "proposal":
      return typeof data["proposal_id"] === "string"
        ? {
            type: "proposal",
            proposal_id: data["proposal_id"],
            diff: typeof data["diff"] === "string" ? data["diff"] : "",
            change_note: typeof data["change_note"] === "string" ? data["change_note"] : "",
            sections: Array.isArray(data["sections"])
              ? data["sections"].filter((s): s is string => typeof s === "string")
              : [],
            supersedes: typeof data["supersedes"] === "string" ? data["supersedes"] : null,
          }
        : null;
    case "bio_proposal":
      return typeof data["proposal_id"] === "string"
        ? {
            type: "bio_proposal",
            proposal_id: data["proposal_id"],
            bio: typeof data["bio"] === "string" ? data["bio"] : "",
            change_note: typeof data["change_note"] === "string" ? data["change_note"] : "",
            supersedes: typeof data["supersedes"] === "string" ? data["supersedes"] : null,
          }
        : null;
    case "public_line_proposal":
      return typeof data["proposal_id"] === "string"
        ? {
            type: "public_line_proposal",
            proposal_id: data["proposal_id"],
            public_line: typeof data["public_line"] === "string" ? data["public_line"] : "",
            change_note: typeof data["change_note"] === "string" ? data["change_note"] : "",
            supersedes: typeof data["supersedes"] === "string" ? data["supersedes"] : null,
          }
        : null;
    case "done":
      return {
        type: "done",
        message_id: typeof data["message_id"] === "string" ? data["message_id"] : "",
        proposal_id: typeof data["proposal_id"] === "string" ? data["proposal_id"] : null,
        onboarding:
          data["onboarding"] && typeof data["onboarding"] === "object"
            ? normaliseOnboarding(data["onboarding"])
            : null,
      };
    case "error":
      return {
        type: "error",
        code: typeof data["code"] === "string" ? data["code"] : "UNKNOWN",
        exhausted: null,
      };
    default:
      return null;
  }
}

/**
 * The owner's verdict on a bio the doorman drafted (FR-3.8.1). Accept is the
 * WRITE — the server re-screens the stored sentence on the way through — and
 * dismiss closes the draft without touching the door. Neither carries the text:
 * the proposal holds it, so the browser cannot substitute one.
 */
export type BioVerdictResult =
  | { ok: true }
  | { ok: false; code: "UNAUTHENTICATED" | "ALREADY_RESOLVED" | "NOT_FOUND" | "UNKNOWN" };

async function bioVerdict(
  proposalId: string,
  verb: "accept" | "dismiss",
  /** Which public text the card drafts: the bio, or the public line (R-0925-2 (5)). */
  target: "bio" | "public-line" = "bio",
): Promise<BioVerdictResult> {
  const r = await call(() => post(`/api/v1/door/${target}/proposals/${proposalId}/${verb}`, {}));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.ok) return { ok: true };
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (r.res.status === 409) return { ok: false, code: "ALREADY_RESOLVED" };
  if (r.res.status === 404) return { ok: false, code: "NOT_FOUND" };
  return { ok: false, code: "UNKNOWN" };
}

export function acceptBioProposal(proposalId: string): Promise<BioVerdictResult> {
  return bioVerdict(proposalId, "accept");
}

export function dismissBioProposal(proposalId: string): Promise<BioVerdictResult> {
  return bioVerdict(proposalId, "dismiss");
}

/** The same two verbs on a public line Nob drafted (R-0925-2 (5)). */
export function acceptPublicLineProposal(proposalId: string): Promise<BioVerdictResult> {
  return bioVerdict(proposalId, "accept", "public-line");
}

export function dismissPublicLineProposal(proposalId: string): Promise<BioVerdictResult> {
  return bioVerdict(proposalId, "dismiss", "public-line");
}
